import React from 'react';

import { Document, Page, Text, View, Image, StyleSheet, Font, Link } from '@react-pdf/renderer';
import type { TFunction } from 'i18next';

import { IEventByDateItem } from '@entities/eventJournal';
import { TEstimate, TExport } from '@entities/eventJournal/model/types';

import { renderRichText } from './renderRichText';

Font.register({
    family: 'NotoSans',
    fonts: [{ src: '/fonts/NotoSans-Regular.ttf', fontWeight: 400 }],
});
Font.register({
    family: 'DejaVuSans',
    fonts: [{ src: '/fonts/DejaVuSans.ttf', fontWeight: 400 }],
});
Font.register({
    family: 'Tahoma',
    fonts: [
        { src: '/fonts/tahomaRegular.ttf', fontWeight: 400 },
        { src: '/fonts/tahomaRegular.ttf', fontWeight: 500 },
        { src: '/fonts/Tahoma_reg.ttf', fontWeight: 700 },
    ],
});

const styles = StyleSheet.create({
    page: {
        paddingTop: 15,
        paddingBottom: 50,
        paddingHorizontal: 28,
        fontFamily: 'Tahoma',
        fontWeight: 400,
        fontSize: 10,
        color: '#111',
        display: 'flex',
        flexDirection: 'column',
    },
    companyBlock: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: 'row',
        height: 78.75,
        gap: 18,
        boxSizing: 'border-box',
        borderBottom: '0.75 solid #f5f5f5',
    },
    companyContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4.5,
    },
    companyName: {
        fontSize: 9,
        fontWeight: 700,
    },
    companyAddress: {
        fontSize: 7.5,
        fontWeight: 400,
    },
    companyContacts: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        fontSize: 7.5,
        fontWeight: 400,
        color: '#1094F7',
    },
    companyLogo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4.5,
    },
    companyImg: {
        objectFit: 'cover',
        width: 105,
        height: 78.75,
        borderRadius: 3,
    },
    report: {
        marginTop: 15,
        marginBottom: 15,
        display: 'flex',
        flexDirection: 'row',
        maxWidth: 545,
        gap: 6,
        alignItems: 'flex-end',
    },
    reportHeader: {
        fontSize: 13.5,
        fontWeight: 700,
        marginBottom: 1,
    },
    reportPeriod: {
        fontSize: 8.5,
        color: '#252733',
        marginBottom: 2,
    },
    reportContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 7.5,
    },
    reportContentItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 6,
    },
    reportContentTitle: {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: 7.5,
        minWidth: 42,
        flexShrink: 0,
    },
    reportContentValue: {
        fontWeight: 400,
        fontSize: 7.5,
        flexGrow: 1,
        flexShrink: 1,
        maxWidth: '150px',
    },

    projectName: {
        fontWeight: 700,
    },
    multiLineCell: {
        flexDirection: 'row',
        gap: 15,
    },
    multiLineCellItem: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        marginTop: 15,
        paddingBottom: 25,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        borderBottom: '0.75 solid #f5f5f5',
    },
    footer: {
        width: '100%',
        maxWidth: 545,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'absolute',
        bottom: 18,
        left: 28,
        right: 28,
        fontSize: 7,
        color: '#6b7280',
    },
    link: {
        color: '#6b7280',
        textDecoration: 'none',
    },
    section: { display: 'flex', flexDirection: 'column', gap: 16 },
    date: { fontSize: 10, fontWeight: 700, marginTop: 15 },
    card: { padding: 10, marginBottom: 8, backgroundColor: 'red' },
    textCard: { display: 'flex', gap: 8, flexDirection: 'row', maxWidth: 500 },
    row: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap' },
    textGroup: { display: 'flex', flexDirection: 'column', gap: 16 },
    photoGroup: { display: 'flex', flexDirection: 'column', gap: 16 },
    photosRow: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        width: '100%',
    },
    photoItem: {
        objectFit: 'contain',
        // backgroundColor: '#f3f4f6',
        height: 174,
        borderRadius: 12,
    },
    photo1: { width: '100%' },
    photo2: { width: '48%' },
    photo3: { width: '32%' },
    photoGrid: { width: '32%' },
    textItem: { display: 'flex', flexDirection: 'row' },
    avatar: { width: 13.5, height: 13.5, borderRadius: 30, backgroundColor: '#e5e7eb' },
    authorName: {
        fontSize: 9,
        color: '#6b7280',
        fontWeight: 400,
    },
    authorRole: {
        backgroundColor: '#f4f4f4',
        color: '#818998',
        borderRadius: 3,
        paddingHorizontal: 3,
        paddingVertical: 3,
    },
    icon: {
        width: 13.5,
        height: 13.5,
        borderRadius: 30,
        backgroundColor: '#e5e7eb',
        flexShrink: 0,
    },
    textCol: { flexGrow: 1, flexShrink: 1, marginTop: 1.5 },
    textContent: { display: 'flex', gap: 6, flexDirection: 'column' },
    estimate: { fontSize: 9, color: '#6b7280', marginLeft: '8px', maxWidth: 440 },
    textAuthor: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        fontSize: 9,
        marginLeft: 19.5,
    },
    photoAuthor: {
        marginTop: 8,
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 7.5,
        width: '100%',
        overflow: 'hidden',
    },
    description: {
        display: 'flex',
        flexDirection: 'row',
        gap: 6,
    },
    muted: { color: '#6b7280' },
});

interface JournalPdfProps {
    items: IEventByDateItem[];
    exportData: TExport;
    dateFrom: string;
    dateTo: string;
    estimateEntities: { [p: string]: TEstimate };
    t: TFunction<'translation', undefined>;
}

export const JournalPdf = ({
    items,
    dateTo,
    dateFrom,
    estimateEntities,
    exportData,
    t,
}: JournalPdfProps) => {
    return (
        <Document>
            <Page size="A4" style={styles.page} wrap>
                <View>
                    <View style={styles.companyBlock}>
                        <View style={styles.companyContent}>
                            <Text style={styles.companyName}>{exportData?.companyName}</Text>
                            {!!exportData?.companyAddress && (
                                <Text style={styles.companyAddress}>
                                    {exportData?.companyAddress}
                                </Text>
                            )}
                            {!!exportData?.companyCite ||
                                (!!exportData?.companyPhone && (
                                    <View style={styles.companyContacts}>
                                        <Text>{exportData?.companyCite}</Text>
                                        <Text>{exportData?.companyPhone}</Text>
                                    </View>
                                ))}
                        </View>
                        <View style={styles.companyLogo}>
                            <Image style={styles.companyImg} src={exportData?.logoUrl} />
                        </View>
                    </View>
                    <View style={styles.report}>
                        <View>
                            <Text style={styles.reportHeader}>
                                {t('JournalPdf.return.Document.Page.View.header')}
                            </Text>
                        </View>
                        <Text style={styles.reportPeriod}>
                            {t('JournalPdf.return.Document.Page.View.periodFrom')} {dateFrom}{' '}
                            {t('JournalPdf.return.Document.Page.View.periodTo')} {dateTo}
                        </Text>
                    </View>
                    <View style={[styles.reportContent]}>
                        <View style={styles.reportContentItem}>
                            <Text style={styles.reportContentTitle}>
                                {t(
                                    'JournalPdf.return.Document.Page.View.reportContent.reportContentTitle.object'
                                )}
                            </Text>
                            <Text style={[styles.reportContentValue, styles.projectName]}>
                                {exportData?.projectName}
                            </Text>
                        </View>
                        <View style={styles.reportContentItem}>
                            <Text style={styles.reportContentTitle}>
                                {t(
                                    'JournalPdf.return.Document.Page.View.reportContent.reportContentTitle.address'
                                )}
                            </Text>
                            <Text style={[styles.reportContentValue, { maxWidth: 500 }]}>
                                {exportData?.projectAddress}
                            </Text>
                        </View>
                    </View>
                    <View style={[styles.multiLineCell]}>
                        <View style={styles.multiLineCellItem}>
                            <Text style={styles.reportContentTitle}>
                                {t(
                                    'JournalPdf.return.Document.Page.View.reportContent.reportContentTitle.customer'
                                )}
                            </Text>
                            <Text style={styles.reportContentValue}>
                                {exportData?.customerName}
                            </Text>
                        </View>
                        <View style={styles.multiLineCellItem}>
                            <Text style={[styles.reportContentTitle]}>
                                {t(
                                    'JournalPdf.return.Document.Page.View.reportContent.reportContentTitle.executor'
                                )}
                            </Text>
                            <Text style={styles.reportContentValue}>{exportData?.legalEntity}</Text>
                        </View>
                    </View>
                </View>

                {items?.map((group, idx) => (
                    <View key={idx} style={styles.section} wrap>
                        <Text style={styles.date}>{group.date}</Text>

                        {group?.events?.text?.length > 0 && (
                            <View style={styles.textGroup}>
                                {[...group.events.text].reverse().map((text, j) => (
                                    <View key={j} style={[styles.textCard]} wrap={false}>
                                        <View style={[styles.textCol, styles.textContent]}>
                                            <View style={styles.description}>
                                                <Image src={text?.iconUrlPng} style={styles.icon} />
                                                {text?.description &&
                                                    renderRichText(text?.description)}
                                            </View>
                                            <View style={styles.textAuthor}>
                                                <Text style={styles.authorName}>
                                                    {text?.author?.name}
                                                </Text>
                                                {text?.author?.type === 'subcontractor' && (
                                                    <Text style={styles.authorRole}>
                                                        {t(
                                                            'FilterByAuthor.AuthorBadge.labelMapping.subcontractor.tooltip'
                                                        )}
                                                    </Text>
                                                )}

                                                {text?.estimateId && (
                                                    <Text style={styles.estimate}>
                                                        {`${t(
                                                            'JournalPdf.return.Document.Page.View.estimate'
                                                        )} ${
                                                            estimateEntities[text.estimateId]?.name
                                                        }`}
                                                    </Text>
                                                )}
                                            </View>
                                        </View>
                                    </View>
                                ))}
                            </View>
                        )}

                        {group?.events?.photo?.length > 0 && (
                            <View style={styles.photosRow}>
                                {[...group.events.photo].reverse().map((p, i) => (
                                    <View
                                        key={i}
                                        style={[
                                            group?.events?.photo?.length === 1 && styles.photo1,
                                            group?.events?.photo?.length === 2 && styles.photo2,
                                            group?.events?.photo?.length === 3 && styles.photo3,
                                            group?.events?.photo?.length > 3 && styles.photoGrid,
                                        ]}
                                        wrap={false}
                                    >
                                        <Image src={p?.imageUrl} style={[styles.photoItem]} />
                                        {p?.description ? (
                                            <Text
                                                style={[
                                                    {
                                                        fontSize: 9,
                                                        marginTop: 4,
                                                        width: '100%',
                                                        textAlign: 'center',
                                                    },
                                                ]}
                                            >
                                                {renderRichText(p?.description)}
                                            </Text>
                                        ) : null}
                                        {p?.author ? (
                                            <View style={styles.photoAuthor}>
                                                <Text style={styles.authorName}>
                                                    {p.author.name}
                                                </Text>
                                            </View>
                                        ) : null}
                                    </View>
                                ))}
                            </View>
                        )}
                    </View>
                ))}
                <View style={styles.footer} fixed>
                    <Text>{t('JournalPdf.return.Document.Page.footer.created')}</Text>
                    <Link style={styles.link} src={`${ENV.BASE_URL}`}>
                        <Text></Text>
                    </Link>
                    <Text
                        render={({ pageNumber, totalPages }) =>
                            `${t('JournalPdf.return.Document.Page.footer.page')} ${pageNumber} ${t(
                                'JournalPdf.return.Document.Page.footer.pageOf'
                            )} ${totalPages}`
                        }
                    />
                </View>
            </Page>
        </Document>
    );
};
